import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy,
  where,
  limit
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { 
  UserPlus, 
  IdCard, 
  Briefcase, 
  Calendar, 
  Check, 
  Loader2,
  Info,
  School,
  X,
  Calendar as CalendarIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import BirthDatePicker from './BirthDatePicker';

interface StaffSelfEntryProps {
  type: 'educational' | 'assistant';
}

const StaffSelfEntry: React.FC<StaffSelfEntryProps> = ({ type }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nationalId: '',
    birthDate: '',
    phone: '',
    relativeName: '',
    relativePhone: '',
    role: '',
    isSubstitute: false,
    schedule: {} as { [day: string]: string[] }
  });
  const [roles, setRoles] = useState<string[]>([
    'מורה',
    'סייע/ת',
    'אחות',
    'מטפל/ת רגשי',
    'קלינאי/ת תקשורת',
    'מרפא/ה בעיסוק',
    'פסיכולוג/ית',
    'יועץ/ת חינוכי/ת',
    'מורה לשילוב',
    'מנהל/ת',
    'מזכיר/ה'
  ]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeScheduleDay, setActiveScheduleDay] = useState('ראשון');

  const daysOfWeek = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי'];
  const schoolHours = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const q = query(collection(db, 'staffRoles'), orderBy('name'));
      const snapshot = await getDocs(q);
      const dbRoles = snapshot.docs.map(doc => doc.data().name);
      
      // Combine defaults with DB roles, removing duplicates
      const defaultRoles = [
        'מורה', 'סייע/ת', 'אחות', 'מטפל/ת רגשי', 'קלינאי/ת תקשורת', 
        'מרפא/ה בעיסוק', 'פסיכולוג/ית', 'יועץ/ת חינוכי/ת', 
        'מורה לשילוב', 'מנהל/ת', 'מזכיר/ה'
      ];
      
      const combined = Array.from(new Set([...defaultRoles, ...dbRoles])).sort();
      setRoles(combined);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.nationalId) return;

    if (formData.nationalId.length !== 9) {
      alert('תעודת זהות חייבת להכיל 9 ספרות');
      return;
    }

    setLoading(true);
    try {
      // Check for existing staff with same ID or same full name
      const fullName = `${formData.firstName} ${formData.lastName}`;
      const qId = query(
        collection(db, 'staff'), 
        where('nationalId', '==', formData.nationalId), 
        where('active', '==', true),
        limit(1)
      );
      const qName = query(
        collection(db, 'staff'),
        where('fullName', '==', fullName),
        where('active', '==', true),
        limit(1)
      );

      const [snapshotId, snapshotName] = await Promise.all([
        getDocs(qId),
        getDocs(qName)
      ]);

      if (!snapshotId.empty) {
        alert('כבר קיים איש צוות עם תעודת זהות זו במערכת. אם מדובר בטעות, פנה למנהל המערכת.');
        setLoading(false);
        return;
      }

      if (!snapshotName.empty) {
        const confirmMsg = `כבר קיים איש צוות עם השם "${fullName}" במערכת. האם אתה בטוח שברצונך להוסיף רשומה חדשה?`;
        if (!window.confirm(confirmMsg)) {
          setLoading(false);
          return;
        }
      }

      const finalType = formData.role === 'אחות' ? 'educational' : type;
      console.log("Submitting staff registration:", { ...formData, type: finalType });

      await addDoc(collection(db, 'staff'), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        nationalId: formData.nationalId,
        birthDate: formData.birthDate,
        phone: formData.phone,
        relativeName: formData.relativeName,
        relativePhone: formData.relativePhone,
        role: formData.role,
        isSubstitute: formData.isSubstitute,
        schedule: formData.schedule,
        type: finalType,
        active: true,
        createdAt: new Date().toISOString(),
        isSelfEntry: true
      });
      setSubmitted(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'staff');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" dir="rtl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-10 rounded-3xl shadow-xl border border-indigo-100 text-center max-w-md w-full"
        >
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">תודה רבה!</h2>
          <p className="text-slate-600 mb-8">הפרטים שלך נקלטו בהצלחה במערכת בית הספר.</p>
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold hover:bg-indigo-700 transition-all"
          >
            הזנת פרטים נוספים
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-indigo-600 text-white rounded-2xl mb-4 shadow-lg shadow-indigo-200">
            <School size={32} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">טופס קליטת איש צוות</h1>
          <p className="text-slate-500 mt-2">
            ברוכים הבאים! אנא מלאו את פרטיכם האישיים ואת מערכת השעות השבועית שלכם.
          </p>
        </div>

        {type === 'assistant' && (
          <div className="bg-amber-50 border border-amber-200 p-6 rounded-2xl mb-8 flex gap-4">
            <div className="text-amber-600 shrink-0">
              <Info size={24} />
            </div>
            <div>
              <h4 className="font-bold text-amber-900 mb-1">טיפים למילוי הטופס (צוות סייעות):</h4>
              <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
                <li>הקפידו להזין תעודת זהות תקינה (9 ספרות).</li>
                <li>במערכת השעות, סמנו את כל השיעורים בהם אתם נוכחים בבית הספר.</li>
                <li>אם אתם עובדים כממלאי מקום, סמנו את התיבה המתאימה בסוף הטופס.</li>
              </ul>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-indigo-900 border-b pb-4">
              <UserPlus size={24} />
              פרטים אישיים
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם פרטי</label>
                <div className="relative">
                  <UserPlus className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם פרטי"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם משפחה</label>
                <div className="relative">
                  <UserPlus className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם משפחה"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">תעודת זהות</label>
                <div className="relative">
                  <IdCard className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.nationalId}
                    onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-mono"
                    placeholder="9 ספרות"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <BirthDatePicker
                  label="תאריך לידה"
                  value={formData.birthDate}
                  onChange={(value) => setFormData({ ...formData, birthDate: value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">טלפון נייד</label>
                <div className="relative">
                  <IdCard className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="05X-XXXXXXX"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">תפקיד</label>
                <div className="relative">
                  <Briefcase className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                  >
                    <option value="">בחר תפקיד...</option>
                    {roles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                    <option value="אחר">אחר</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם קרוב משפחה</label>
                <div className="relative">
                  <UserPlus className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    value={formData.relativeName}
                    onChange={(e) => setFormData({ ...formData, relativeName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם קרוב משפחה"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">טלפון קרוב משפחה</label>
                <div className="relative">
                  <IdCard className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="tel"
                    value={formData.relativePhone}
                    onChange={(e) => setFormData({ ...formData, relativePhone: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="05X-XXXXXXX"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-indigo-900 border-b pb-4">
              <Calendar size={24} />
              מערכת שעות שבועית
            </h3>
            
            <div className="space-y-6">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {daysOfWeek.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setActiveScheduleDay(day);
                      // If no hours are selected for this day, set default 1-8
                      if (!formData.schedule?.[day] || formData.schedule[day].length === 0) {
                        setFormData({
                          ...formData,
                          schedule: {
                            ...formData.schedule,
                            [day]: ['1', '2', '3', '4', '5', '6', '7', '8']
                          }
                        });
                      }
                    }}
                    className={`px-6 py-3 rounded-2xl text-sm font-bold transition-all whitespace-nowrap ${
                      activeScheduleDay === day
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                        : 'bg-slate-50 text-slate-500 border border-slate-200 hover:border-indigo-300'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>

              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-500 font-bold">סמנו את שעות העבודה שלכם ביום {activeScheduleDay}:</p>
                  {(formData.schedule?.[activeScheduleDay]?.length || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          schedule: {
                            ...formData.schedule,
                            [activeScheduleDay]: []
                          }
                        });
                      }}
                      className="text-sm font-bold text-red-500 hover:text-red-700 transition-colors flex items-center gap-1"
                    >
                      <X size={16} />
                      נקה יום זה
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-3">
                  {schoolHours.map(hour => {
                    const isSelected = formData.schedule?.[activeScheduleDay]?.includes(hour);
                    return (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => {
                          const currentDayHours = formData.schedule?.[activeScheduleDay] || [];
                          const newDayHours = isSelected
                            ? currentDayHours.filter(h => h !== hour)
                            : [...currentDayHours, hour];
                          setFormData({
                            ...formData,
                            schedule: {
                              ...formData.schedule,
                              [activeScheduleDay]: newDayHours
                            }
                          });
                        }}
                        className={`aspect-square rounded-xl flex items-center justify-center text-lg font-bold transition-all ${
                          isSelected
                            ? 'bg-indigo-600 text-white shadow-md scale-105'
                            : 'bg-white text-slate-400 border border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        {hour}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {type === 'assistant' && (
            <div className="flex items-center gap-4 p-6 bg-white rounded-3xl border border-slate-100 shadow-sm">
              <input
                type="checkbox"
                id="isSubstitute"
                checked={formData.isSubstitute}
                onChange={(e) => setFormData({ ...formData, isSubstitute: e.target.checked })}
                className="w-6 h-6 rounded text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="isSubstitute" className="font-bold text-slate-700 cursor-pointer text-lg">
                אני עובד/ת כממלא/ת מקום
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-5 rounded-3xl font-bold text-xl hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-3 shadow-xl shadow-indigo-100"
          >
            {loading ? <Loader2 className="animate-spin" size={24} /> : <Check size={24} />}
            <span>שליחת פרטים וסיום</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default StaffSelfEntry;
